import React, { useEffect, useId, useRef, useState } from 'react';
import { CubeIcon, DocumentTextIcon, FilmIcon, MusicalNoteIcon, PhotoIcon } from '@heroicons/react/24/solid';
import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { useBool } from '#lib/hooks/use-boolean-state.js';
import { bytesToSize } from '#lib/utils/units.js';
import { isFunction } from '#lib/utils/function.js';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import Button from '#lib/components/button.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import validateFile from '#lib/helpers/file.js';
import toast from 'react-hot-toast';
import { DocumentObject } from '#components/document-modal.jsx';
import { LoadingIndicator } from './loading.jsx';
import { newIdPrefix } from '#helpers/helpers.js';
import { Icon } from '@iconify-icon/react';

const iconMap = {
    application: CubeIcon,
    audio: MusicalNoteIcon,
    font: DocumentTextIcon,
    image: PhotoIcon,
    model: CubeIcon,
    text: DocumentTextIcon,
    video: FilmIcon
};

export default function FileDnD(props) {
    const {
        className,
        helperText = _t.global.dragFile(),
        label,
        type,
        onChange: providedOnChange,
        onUpdateFiles,
        existingFiles = [],
        sourceType,
        onClear,
        children,
        rounded = false,
        accept,
        multiple = false,
        name,
        ...rest
    } = props;

    const hasExistingFiles = existingFiles.length > 0 && existingFiles.every(Boolean);

    const [files, setFiles] = useState(hasExistingFiles ? existingFiles : []);
    useEffect(() => {
        // If existing files are provided, set them as the initial files, otherwise set files already in state, or an empty array
        setFiles(hasExistingFiles ? existingFiles : []);
    }, [hasExistingFiles]);

    const hasFiles = files.length > 0;

    const file = files[0];

    const id = useId();
    const helperId = useId();

    const [isHovering, onMouseOver, onMouseOut] = useBool(false);
    const [isDragging, onDragEnter, onDragLeave] = useBool(false);

    function onChange(e) {
        onDragLeave();
        const { files } = e.target;
        if (files.length === 0) return;
        var filesArray = Array.from(files);
        filesArray.forEach((file) => {
            if (!validateFile(file.type, accept)) return toast.error(_t.global.invalidFileType());
        });
        if (isFunction(onUpdateFiles)) onUpdateFiles(e, files);
        if (isFunction(providedOnChange)) providedOnChange(e, sourceType);
        setFiles(filesArray);
    }

    function onHelperClick() {
        const inputEl = inputRef.current;
        if (!inputEl) return;
        inputEl.focus();
        inputEl.click();
    }

    const inputRef = useRef();

    function clear() {
        if (isFunction(onClear)) onClear(files);
        if (isFunction(onUpdateFiles)) onUpdateFiles();
        setFiles([]);
        const { current: input } = inputRef;
        if (!input) return;
        input.value = null;
    }

    const dragEvents = {
        onDragEnter,
        onMouseOver,
        onMouseOut
    };

    const helperProps = {
        onClick: onHelperClick,
        id: helperId,
        className: addClasses('area-span-full p-3'),
        children: _t.global.addFile(),
        ...dragEvents,
        isDragging,
        Icon: iconMap[type] ?? PhotoIcon,
        subText: helperText
    };

    const clearButtonProps = {
        onClick: clear,
        rounded
    };

    const inputProps = {
        id,
        onChange,
        onDragLeave,
        'aria-describedby': helperId,
        accept,
        multiple
    };

    return (
        <div
            className={mergeClasses('flex flex-col gap-1 w-full', className)}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
        >
            {label && (
                <label className="block text-sm font-medium text-neutral-700" htmlFor={id}>
                    {label}
                </label>
            )}

            <div className="w-full h-full relative grid rounded-md p-[1px]">
                {isFunction(children) ? (
                    <div
                        className={`relative area-span-full flex items-center h-full overflow-hidden rounded-${rounded ? 'full' : 'md'
                            }`}
                    >
                        {children({
                            hasFiles,
                            file,
                            files,
                            clear,
                            dragEvents,
                            helperProps,
                            clearButtonProps
                        })}
                    </div>
                ) : (
                    <React.Fragment>
                        {hasFiles && (
                            <React.Fragment>
                                <FileInfo name={files[0].name} type={files[0].type} size={files[0].size} />

                                <ClearButton onClick={clear}>
                                    {/* {_t.components.images.removeUploadedImage()} */}
                                </ClearButton>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                )}

                <SVGBackground
                    animate={isDragging}
                    complete={hasFiles}
                    appearAsButton={isHovering}
                    rx={rounded ? 100 : 6}
                    className={addClasses('area-span-full h-full w-full')}
                />

                {!hasFiles && <Helper {...helperProps} />}
                <input
                    ref={inputRef}
                    type="file"
                    className={cn(hasFiles ? '-z-10' : 'z-10', 'peer absolute h-full w-full opacity-0 cursor-pointer')}
                    {...inputProps}
                />

                <input name={props.name} type="hidden" />
            </div>
        </div>
    );
}

export function ClearButton(props) {
    const { children = 'Rimuovi immagine caricata', rounded, ...rest } = props;
    return (
        <Button
            className={addClasses(`absolute ${rounded ? '-right-3 -top-0' : 'right-2 top-2'}`)}
            look="circular"
            size="xs"
            {...rest}
        >
            <span className="sr-only">{children}</span>
            <Icon
                className="cursor-pointer"
                icon="ph:trash"
                width="1.2rem"
                height="1.2rem"
            />
        </Button>
    );
}

export function FullScreenButton(props) {
    const { children = 'Rimuovi immagine caricata', className, rounded, ...rest } = props;
    return (
        <Button
            className={addClasses(cn('absolute', rounded ? '-left-3 -top-0' : 'left-2 top-2', className && className))}
            look="circular"
            size="xs"
            {...rest}
        >
            <span className="sr-only">{children}</span>
            <ArrowsPointingOutIcon className="h-5 w-5" />
        </Button>
    );
}

function Helper(props) {
    const { className, children, subText, Icon, isDragging, onClick, ...rest } = props;

    return (
        <div
            onClick={onClick}
            className={mergeClasses(
                cn(
                    isDragging ? 'text-primary-700' : 'text-neutral-600',
                    'h-full inline-flex w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md text-sm'
                ),
                className
            )}
            {...rest}
        >
            <span className="inline-flex items-center gap-1 font-semibold">
                {Icon && (
                    <Icon
                        className={cn(
                            isDragging ? 'text-primary-700' : 'text-neutral-700',
                            'h-6 w-6 flex-shrink-0 -translate-x-0.5'
                        )}
                    />
                )}
                {children}
            </span>
            {subText && <p className="text-center">{subText}</p>}
        </div>
    );
}

function FileInfo(props) {
    const { name, type: mimeType, size } = props;
    const [type] = mimeType.split('/');

    const Icon = iconMap[type] ?? CubeIcon;

    return (
        <article className="relative flex flex-col items-center justify-center gap-2 p-4">
            <SVGBackground className={addClasses('absolute area-span-full h-full w-full')} />
            <Icon className="icon fill-primary-600 z-10" />
            <p className="w-full text-center text-sm text-neutral-800 z-10">
                <span className="break-words">{name}</span>&nbsp;
                {size > 0 && <span className="text-xs text-neutral-600">({bytesToSize(size)})</span>}
            </p>
        </article>
    );
}

export function SVGBackground(props) {
    const { animate, complete, appearAsButton, strokeWidth = 2, rx = 6, className, ...rest } = props;

    const rectClassName = cn(
        'transition-all animate-dash',
        appearAsButton ? 'fill-white' : 'fill-neutral-50',
        animate ? 'animation-running fill-primary-100 stroke-primary-300' : 'animation-pause stroke-neutral-300'
    );

    const isSolid = appearAsButton || (complete && !animate);

    return (
        <svg
            preserveAspectRatio="none"
            overflow="visible"
            height={rx === 100 ? '200px' : '0'}
            width={rx === 100 ? '200px' : '0'}
            className={mergeClasses(cn(appearAsButton && rx !== 100 && 'shadow-md', 'rounded-md'), className)}
            {...rest}
        >
            <rect
                className={rectClassName}
                width="100%"
                height="100%"
                style={{ '--stroke-dashoffset': strokeWidth * 6 }}
                vectorEffect="non-scaling-stroke"
                rx={rx}
                strokeWidth={appearAsButton ? 1 : strokeWidth}
                strokeDasharray={isSolid ? 'none' : strokeWidth * 3}
            />
        </svg>
    );
}

FileDnD.Helper = Helper;

export function DocumentPreview(props) {
    const { document, clear, className, handleModal } = props;
    let src = document?.src;

    if (document?.id.startsWith(newIdPrefix) && !document.isLoading) src = `${MEDIA_URL}/${src}`;
    const [isHovering, onMouseOver, onMouseOut] = useBool(false);

    return (
        <div className={mergeClasses('w-full h-full p-0', className)} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
            <div className="relative w-full h-full overflow-auto">
                {document.isLoading ? (
                    <div className="w-full h-full inset-0 flex items-center justify-center">
                        <LoadingIndicator />
                    </div>
                ) : (
                    <DocumentObject src={`${src}#toolbar=0&navpanes=0&&statusbar=0&messages=0&scrollbar=0`} />
                )}
            </div>
            <ClearButton onClick={clear} />

            <FloatingTooltip
                content={_t.edit.fullScreen()}
                refClassName={'absolute left-0 top-0'}
                className={addClasses('ml-6')}
                placement="top"
            >
                <FullScreenButton
                    className={addClasses(cn(isHovering && 'hover:text-primary-600'))}
                    onClick={handleModal}
                />
            </FloatingTooltip>
        </div>
    );
}
